import { IonButton as IonicIonButton } from '@ionic/react'
import { forwardRef, HTMLAttributes, useMemo } from 'react'
import type { ComponentProps, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import {
  ButtonContent,
  EndDecoratorWrapper,
  IonButton,
  IonSpinner,
  StartDecoratorWrapper,
} from './styled'

export type Variant =
  | 'limeFlooded'
  | 'coralFlooded'
  | 'lightSteelBlueBorder'
  | 'textLink'
  | 'whiteFlooded'
  | 'blueFlooded'
  | 'aliceBlueFlooded'
  | 'contained'

export type Size = 'large'

type ButtonProps = ComponentProps<typeof IonicIonButton> & {
  variant?: Variant
  children: ReactNode
  startDecorator?: ReactNode
  endDecorator?: ReactNode
  loading?: boolean
  fullWidth?: boolean
  uppercase?: boolean
  contentProps?: HTMLAttributes<HTMLDivElement>
  size?: Size
  to?: string
}

export const Button = forwardRef<HTMLIonButtonElement, ButtonProps>(
  (
    {
      variant = 'limeFlooded',
      children,
      startDecorator,
      endDecorator,
      loading,
      disabled,
      fullWidth,
      uppercase,
      contentProps,
      size,
      to,
      ...props
    },
    ref,
  ) => {
    const button = useMemo(
      () => (
        <IonButton
          {...props}
          $variant={variant}
          ref={ref}
          disabled={disabled || loading}
          $fullWidth={fullWidth}
          $uppercase={uppercase}
        >
          <ButtonContent {...contentProps} $size={size}>
            {startDecorator && (
              <StartDecoratorWrapper>{startDecorator}</StartDecoratorWrapper>
            )}

            {children}

            {endDecorator && (
              <EndDecoratorWrapper>
                {loading ? <IonSpinner name='bubbles' /> : endDecorator}
              </EndDecoratorWrapper>
            )}

            {!endDecorator && loading && (
              <EndDecoratorWrapper>
                <IonSpinner name='bubbles' />
              </EndDecoratorWrapper>
            )}
          </ButtonContent>
        </IonButton>
      ),
      [
        children,
        contentProps,
        disabled,
        endDecorator,
        fullWidth,
        loading,
        props,
        ref,
        size,
        startDecorator,
        uppercase,
        variant,
      ],
    )

    return to ? (
      <Link to={to} style={{ lineHeight: '0px', textDecoration: 'none' }}>
        {button}
      </Link>
    ) : (
      <>{button}</>
    )
  },
)

Button.displayName = 'Button'
